// Scrolls a horizontally scrolling box while something is dragged near its
// left or right edge, so a column can be moved (or its edge dragged) beyond
// the visible part of a wide table.

const EDGE_ZONE = 48;
const MAX_STEP = 18;

// A box the browser scrolls (or the script may scroll) horizontally. Its
// content need not overflow yet: SimpleBar keeps `overflow-x: hidden` until
// it does, and a column dragged wider is what makes it overflow.
const canScrollHorizontally = (el) => {
    const { overflowX } = getComputedStyle(el);
    return overflowX !== 'visible' && overflowX !== 'clip';
};

/**
 * The nearest ancestor of `el` that scrolls horizontally, or null.
 * @param {HTMLElement} el - Element inside the scroll box.
 * @returns {HTMLElement|null} the scroll box
 */
export function findHorizontalScroller(el) {
    for (let node = el?.parentElement; node; node = node.parentElement) {
        if (canScrollHorizontally(node)) return node;
    }
    return null;
}

/**
 * Starts edge auto-scrolling of `scroller` for one drag. Feed it the
 * pointer's x from every move; call `stop` when the drag ends.
 *
 * @param {HTMLElement} scroller - The horizontally scrolling box.
 * @param {Function} [getRightEdge] - Returns the viewport x where the right
 *   zone ends — the left edge of a pinned column, so the zone is not hidden
 *   under it. Defaults to the box's right edge.
 * @param {Object} [options] - Options.
 * @param {boolean} [options.leftEdge] - Scroll at the left edge as well
 *   (default); off for a drag that only means to go right.
 * @param {Function} [options.beforeScroll] - `(delta)` ahead of each step,
 *   to make room for it: a column edge dragged past the end of the table
 *   widens the table by the step it is about to scroll.
 * @returns {Object} `{ update(clientX), stop() }`
 */
export function startDragAutoScroll(
    scroller,
    getRightEdge,
    { leftEdge = true, beforeScroll } = {}
) {
    let clientX = null;
    let frame = 0;

    const step = () => {
        frame = 0;
        if (clientX === null) return;
        const rect = scroller.getBoundingClientRect();
        const right = Math.min(getRightEdge?.() ?? rect.right, rect.right);
        let delta = 0;
        if (leftEdge && clientX < rect.left + EDGE_ZONE) {
            delta = -MAX_STEP * (1 - Math.max(clientX - rect.left, 0) / EDGE_ZONE);
        } else if (clientX > right - EDGE_ZONE) {
            delta = MAX_STEP * (1 - Math.max(right - clientX, 0) / EDGE_ZONE);
        }
        if (delta) {
            beforeScroll?.(delta);
            scroller.scrollLeft += delta;
            frame = requestAnimationFrame(step);
        }
    };

    return {
        update(x) {
            clientX = x;
            if (!frame) frame = requestAnimationFrame(step);
        },
        stop() {
            clientX = null;
            cancelAnimationFrame(frame);
            frame = 0;
        }
    };
}
